import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, View, Platform } from "react-native";
import ThemeContext from "../../../context/ThemeContext";
import { darkTheme } from "../../../config/themes";

// =============================================================================
// Voxcraft marketing typography — must match the v4 mockup. Inter for body,
// Space Grotesk for headings, JetBrains Mono for code/data/meta labels.
// The Google Fonts <link> is in web/index.html.
// =============================================================================
export const FONT_BODY    = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";
export const FONT_DISPLAY = "'Space Grotesk', Inter, sans-serif";
export const FONT_MONO    = "'JetBrains Mono', Menlo, monospace";

/**
 * Forces the marketing surface to render with darkTheme regardless of the
 * user's chosen mode. We rebind ThemeContext.Provider with a frozen value so
 * every nested `useTheme()` reads the dark palette.
 */
export function ForceDarkTheme({ children }) {
  const value = {
    theme: darkTheme,
    mode: "dark",
    resolved: "dark",
    setMode: () => {},
    toggleMode: () => {},
  };
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Reveal-on-scroll wrapper. Mirrors v4's `.reveal` pattern — opacity 0 +
 * 28px translateY, transitions to in-place when the element enters the
 * viewport. Web-only effect (no-op on native — content just renders).
 *
 * Props:
 *   - delay  0–5  (mapped to .delay-1 .. .delay-5 CSS classes)
 *   - style  passthrough wrapper style
 */
export function Reveal({ children, delay = 0, style }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (Platform.OS !== "web") {
      setInView(true);
      return;
    }
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    // react-native-web turns View into a div; findDOMNode is the easy bridge.
    const el = node._node || node;
    if (!el || !el.classList) {
      setInView(true);
      return;
    }
    el.classList.add("voxcraft-reveal");
    if (delay) el.classList.add(`delay-${delay}`);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [delay]);

  return (
    <View ref={ref} style={style}>
      {children}
    </View>
  );
}

/**
 * WebClass — mounts a CSS class on the rendered DOM node on web (no-op on
 * native). Used to wire :hover-based animations from web/index.html that
 * react-native style props can't express.
 *
 * Pass `as={Pressable}` to keep press semantics; otherwise renders a View.
 */
export function WebClass({ className, children, style, onPress }) {
  const ref = useRef(null);
  useEffect(() => {
    if (Platform.OS !== "web") return;
    const el = ref.current && (ref.current._node || ref.current);
    if (!el || !el.classList) return;
    const classes = String(className || "").split(/\s+/).filter(Boolean);
    classes.forEach((c) => el.classList.add(c));
    return () => classes.forEach((c) => el.classList.remove(c));
  }, [className]);
  return (
    <View ref={ref} style={style}>
      {children}
    </View>
  );
}

/**
 * CountUp — animates a number from 0 → `to` once the element enters the
 * viewport. Mirrors v4's `[data-count]` counters. Pass `decimals` for floats
 * (e.g. 99.99). Optional `suffix` is rendered after the number unstyled.
 */
export function CountUp({ to, decimals = 0, duration = 1400, style, suffix = "" }) {
  const ref = useRef(null);
  const [value, setValue] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    if (Platform.OS !== "web") { setValue(to); return; }
    const el = ref.current && (ref.current._node || ref.current);
    if (!el || typeof IntersectionObserver === "undefined") { setValue(to); return; }
    const run = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3);
        setValue(to * eased);
        if (t < 1) requestAnimationFrame(tick);
        else setValue(to);
      };
      requestAnimationFrame(tick);
    };
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { run(); io.unobserve(e.target); } });
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);

  const text = decimals > 0 ? value.toFixed(decimals) : Math.round(value).toString();
  return (
    <Animated.View ref={ref}>
      <View style={{ flexDirection: "row", alignItems: "baseline" }}>
        <View><Animated.Text style={style}>{text}{suffix}</Animated.Text></View>
      </View>
    </Animated.View>
  );
}

/**
 * Pulsing "live" dot. Loops opacity 1 → 0.4 → 1 so it reads as a heartbeat.
 * Pass `color` to override the default green.
 */
export function LiveDot({ color = "#4cb782", size = 6, style }) {
  const opacity = useRef(new Animated.Value(1)).current;
  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(opacity, { toValue: 0.35, duration: 800, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
        Animated.timing(opacity, { toValue: 1,    duration: 800, easing: Easing.inOut(Easing.ease), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [opacity]);

  return (
    <Animated.View
      style={[
        {
          width: size, height: size, borderRadius: size / 2,
          backgroundColor: color,
          // shadow on web → looks like a glow
          shadowColor: color,
          shadowOpacity: 0.7,
          shadowRadius: size * 0.8,
          shadowOffset: { width: 0, height: 0 },
          opacity,
        },
        style,
      ]}
    />
  );
}
